<template>
    <div class="w-full rounded-md bg-white shadow-card">
        <div class="px-6 pb-6 pt-4">
            <div
                class="mb-7 flex flex-wrap items-center justify-between sm:flex-nowrap"
            >
                <div class="flex items-center gap-2">
                    <h4 class="font-medium leading-normal text-gray-800">
                        {{ domain.domain }}
                    </h4>

                    <StatusIndicator
                        :status="domain.active ? 'completed' : 'failed'"
                    />
                </div>

                <div class="flex">
                    <div class="mr-2">
                        <AtomicButton
                            v-if="domain.active"
                            color="white"
                            size="sm"
                            @click="refreshDomain"
                        >
                            <template #icon>
                                <ArrowPathIcon
                                    class="ml-2 mr-2 h-5 w-5"
                                    :class="{ 'animate-spin': refreshing }"
                                />
                            </template>
                        </AtomicButton>
                    </div>
                    <div>
                        <AtomicButton
                            color="white"
                            size="sm"
                            @click="$emit('remove', domain.domain)"
                        >
                            {{ $t("vue.remove_domain") }}
                        </AtomicButton>
                    </div>
                </div>
            </div>

            <div
                class="flex flex-wrap items-start justify-between sm:flex-nowrap"
            >
                <div
                    class="mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                >
                    <p class="text-gray-500">{{ $t("vue.status") }}</p>
                    <p
                        class="font-medium"
                        :class="{
                            'text-green-600': domain.active,
                            'text-red-600': !domain.active,
                        }"
                    >
                        {{
                            domain.active === true
                                ? $t("vue.active")
                                : $t("vue.not_activated")
                        }}
                    </p>
                </div>

                <div
                    class="mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                >
                    <p class="text-gray-500">{{ $t("vue.created") }}</p>
                    <p class="font-medium text-gray-800">
                        {{
                            format(
                                new Date(domain.created_at as string),
                                "d. L. yyyy '('HH:mm:ss')'",
                            )
                        }}
                    </p>
                </div>

                <div
                    class="mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                >
                    <p v-if="domain.active" class="text-gray-500">
                        {{ $t("vue.reports") }}
                    </p>
                    <inertia-link
                        v-if="domain.active"
                        :href="route('reports.show', { domain: domain.domain })"
                        ><p
                            class="font-medium text-custom-blue-500 hover:text-custom-blue-600"
                        >
                            {{ $t("vue.see_reports") }}
                        </p></inertia-link
                    >
                </div>
            </div>

            <div v-if="!domain.active">
                <div
                    v-if="domain.isDnsRecordTxt === false"
                    class="mt-6 rounded-[4px] bg-red-100 px-[14px] py-3"
                >
                    <div class="flex flex-col items-start sm:flex-row">
                        <ExclamationTriangleIcon
                            class="mr-[14px] h-5 w-5 shrink-0 text-red-600"
                        />
                        <div class="mt-2 sm:mt-0">
                            <div class="text-sm font-medium text-red-800">
                                {{ $t("vue.invalid_dns_record_type") }}
                            </div>
                            <div class="mt-[7px] text-sm text-red-700">
                                {{ $t("vue.dmarc_record_cname_error") }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-6 rounded-[4px] bg-yellow-100 px-[14px] py-3">
                    <div class="flex flex-col items-start sm:flex-row">
                        <ExclamationTriangleIcon
                            class="mr-[14px] h-5 w-5 shrink-0 text-yellow-600"
                        />
                        <div class="mt-2 sm:mt-0">
                            <div class="text-sm font-medium text-yellow-800">
                                {{ $t("vue.domain_not_yet_activated") }}
                            </div>
                            <div class="mt-[7px] text-sm text-yellow-700">
                                {{ $t("vue.domain_activation_message") }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4 rounded border border-gray-200 bg-gray-50">
                    <div class="p-4">
                        <h5 class="font-medium leading-normal text-gray-600">
                            {{ $t("vue.dmarc_dns_setup") }}
                        </h5>
                        <div class="mt-2 text-sm text-gray-500">
                            <ol class="list-decimal px-5">
                                <li>
                                    {{ $t("vue.dmarc_setup_step_1") }}
                                </li>
                                <li>
                                    {{ $t("vue.dmarc_setup_step_2") }}
                                </li>
                                <li>
                                    {{ $t("vue.dmarc_setup_step_3") }}
                                </li>
                                <li>
                                    {{ $t("vue.dmarc_setup_step_4") }}
                                    <b>"_dmarc"</b>
                                    {{ $t("vue.dmarc_subdomain_note") }}
                                </li>
                                <li>
                                    {{ $t("vue.dmarc_setup_step_5") }}
                                </li>
                            </ol>
                        </div>

                        <div
                            class="relative mt-4 flex flex-wrap items-center gap-4"
                        >
                            <div class="relative">
                                <textarea
                                    ref="txtRecord"
                                    style="resize: none"
                                    readonly
                                    rows="2"
                                    class="block rounded-md border border-gray-300 bg-white pb-2.5 pl-3 pr-[22px] pt-2 text-sm leading-normal text-gray-800 placeholder-gray-400 shadow-sm transition duration-100 ease-in-out hover:bg-gray-50 focus:border-2 focus:border-gray-600 focus:pb-[9px] focus:pl-[11px] focus:pr-[21px] focus:pt-[7px] focus:outline-hidden lg:w-[380px] xl:w-[470px]"
                                    v-text="domain.recordToAdd"
                                ></textarea>

                                <transition
                                    enter-active-class="transition duration-100 ease-out transform"
                                    enter-from-class="scale-95 opacity-0"
                                    enter-to-class="scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-in transform"
                                    leave-from-class="scale-100 opacity-100"
                                    leave-to-class="scale-95 opacity-0"
                                >
                                    <Tooltip
                                        v-if="copiedSuccess"
                                        class="absolute top-[-55px]"
                                        style="
                                            left: 50%;
                                            transform: translate(-50%, 0);
                                        "
                                    >
                                        {{ $t("vue.copied_to_clipboard") }}
                                    </Tooltip>
                                </transition>
                            </div>

                            <AtomicButton
                                class="relative"
                                color="white"
                                size="sm"
                                @click="copyTextArea"
                            >
                                <template #icon>
                                    <DocumentDuplicateIcon
                                        class="mr-2 h-5 w-5"
                                    />
                                </template>
                                {{ $t("vue.copy_to_clipboard") }}
                            </AtomicButton>
                        </div>

                        <div class="mt-6 flex justify-start">
                            <AtomicButton
                                class="relative"
                                color="yellow"
                                size="md"
                                :disabled="validationLoading"
                                @click="$emit('validate', domain.domain)"
                            >
                                <template #icon>
                                    <DocumentCheckIcon class="mr-2 h-5 w-5" />
                                </template>
                                {{ $t("vue.validate_records") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import type IDomain from "@/types/Domains/IDomain";
import route from "ziggy-js";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/solid";
import {
    ArrowPathIcon,
    DocumentCheckIcon,
    DocumentDuplicateIcon,
} from "@heroicons/vue/24/outline";
import { format } from "date-fns";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps({
    domain: {
        type: Object as PropType<IDomain>,
        default: () => null,
    },
    validationLoading: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
    dnsChecking: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
});

// Get access to translations
const { t } = useTranslations();

const emit = defineEmits(["remove", "validate", "refreshDomains"]);

const { domain } = props;

const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const txtRecord = ref();
const copiedSuccess = ref(false);
const refreshing = ref(false);

const refreshDomain = async () => {
    refreshing.value = true;

    try {
        const result = await axios.post(
            route("domains.domain.refresh", { domain: domain.domain }),
        );

        if (result.status === 204) {
            create({
                title: t("vue.domain_validated"),
                text: t("vue.domain_validated_successfully"),
                type: "success",
            });
            emit("refreshDomains");
        } else {
            create({
                title: t("vue.domain_not_validated"),
                text: t("vue.could_not_validate_domain"),
                type: "warning",
            });
            emit("refreshDomains");
        }
    } catch (e) {
        () => undefined;
    } finally {
        refreshing.value = false;
    }
};

const copyTextArea = () => {
    let element = txtRecord.value as HTMLInputElement;
    element.select();
    document.execCommand("copy");

    copiedSuccess.value = true;
    setTimeout(() => {
        copiedSuccess.value = false;
    }, 1000);
};
</script>

<style></style>
